import Axios from 'axios';
import { store } from '../Store';
import { clearAuthSession, setAuthSession } from '../ReduxSlices/configSlice';
import { AuthTokenResponse } from '../types/auth.types';

/**
 * Sign-in calls to the API's in-house auth endpoints (/Auth/login, /Auth/refresh, ...).
 * Uses its own axios instance, without the app interceptors, so a failed refresh can't loop
 * and routine sign-in errors don't raise the global error toast.
 */
const sessionApi = Axios.create({
  baseURL: window._env_.REACT_APP_WEF_API_BASEURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

export const loginRequest = async (email: string, password: string) => {
  const { data } = await sessionApi.post<AuthTokenResponse>('/Auth/login', { email, password });
  store.dispatch(setAuthSession(data));
  return data;
};

export const changePasswordRequest = async (currentPassword: string, newPassword: string) => {
  const token = store.getState().config.accessToken;
  const { data } = await sessionApi.post<AuthTokenResponse>(
    '/Auth/change-password',
    { currentPassword, newPassword },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  store.dispatch(setAuthSession(data));
  return data;
};

export const logoutRequest = async () => {
  const refreshToken = store.getState().config.refreshToken;
  store.dispatch(clearAuthSession());
  if (refreshToken) {
    try {
      await sessionApi.post('/Auth/logout', { refreshToken });
    } catch {
      // Signing out locally is what matters; the token expires on its own.
    }
  }
};

export const forgotPasswordRequest = async (email: string) => {
  const { data } = await sessionApi.post('/Auth/forgot-password', { email });
  return data;
};

export const resetPasswordRequest = async (token: string, newPassword: string) => {
  const { data } = await sessionApi.post('/Auth/reset-password', { token, newPassword });
  return data;
};

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchanges the stored refresh token for new tokens. Concurrent callers share one request
 * (refresh tokens are single use). Resolves to the new access token, or null when the session
 * can't be renewed (the session is then cleared and the app returns to /login).
 */
export const refreshSession = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;
  const usedRefreshToken = store.getState().config.refreshToken;
  if (!usedRefreshToken) return Promise.resolve(null);

  refreshInFlight = sessionApi
    .post<AuthTokenResponse>('/Auth/refresh', { refreshToken: usedRefreshToken })
    .then(({ data }) => {
      store.dispatch(setAuthSession(data));
      return data.accessToken;
    })
    .catch(() => {
      // Another tab may have rotated the token first and synced it to this tab meanwhile.
      const current = store.getState().config;
      if (current.refreshToken && current.refreshToken !== usedRefreshToken) {
        return current.accessToken;
      }
      store.dispatch(clearAuthSession());
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};
